/**
 * Configurações › Conta › Guia: conectar.
 *
 * Um passo de cada vez, para a pessoa conectar sozinha: escolhe ONDE quer a
 * agenda (iPhone/Mac, Android, Google, IA, Outlook) e segue passos curtos,
 * com os botões de verdade no meio (gerar senha, conectar o Google, copiar o
 * endereço) e um "Conferir agora" no fim. O caminho fica na URL
 * (?caminho=ia&qual=claude), então o convite da agenda e as outras telas
 * abrem direto no lugar certo.
 *
 * Carga: duas leituras ao abrir (integrações e calendários, as mesmas das
 * outras abas) e uma por clique em "Conferir agora". Nada roda sozinho.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import BotaoCopiar from "../../../components/BotaoCopiar";
import { useAuth } from "../../../context/AuthContext";
import { lerCalendarios } from "../../../services/agenda";
import { lerMinhasIntegracoes } from "../../../services/conexoesIa";
import { REGRAS_DA_VIA_DUPLA } from "../tabs/SincronizacaoCalendarios";
import {
  Abas, BlocoGoogle, BlocoSenha, Conferir, Frases, Passo, TELA_CALENDARIO, TELA_IA,
  resultadoDaIa, resultadoDoCalDAV, resultadoDoGoogle,
} from "./BlocosDoGuia";
import { DICA_DOS_MENUS, PASSOS_CALDAV, PASSOS_POR_CALENDARIO, PASSOS_POR_IA } from "./passos";
import "../tabs/IntegracoesIA.css";
import "./guia.css";

const sv = (d) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {d.map((p) => <path key={p} d={p} />)}
  </svg>
);

export const CAMINHOS = [
  { id: "apple", titulo: "iPhone, iPad ou Mac", resumo: "No app Calendário da Apple. Mudou lá, muda aqui.", icone: sv(["M8 2.5h8a1.5 1.5 0 0 1 1.5 1.5v16a1.5 1.5 0 0 1-1.5 1.5H8A1.5 1.5 0 0 1 6.5 20V4A1.5 1.5 0 0 1 8 2.5z", "M11 18.5h2"]) },
  { id: "android", titulo: "Celular Android", resumo: "Pelo Google Agenda, que já vem no Android.", icone: sv(["M7 9.5h10v9a1.5 1.5 0 0 1-1.5 1.5h-7A1.5 1.5 0 0 1 7 18.5z", "M7 9.5a5 5 0 0 1 10 0", "M9.5 6.5L8 4.5", "M14.5 6.5L16 4.5"]) },
  { id: "google", titulo: "Google Agenda", resumo: "No computador e no celular, nos dois sentidos.", icone: sv(["M4 6.5h16v13H4z", "M4 10.5h16", "M8.5 3.5v4", "M15.5 3.5v4", "M9 15l2 2 4-4"]) },
  { id: "ia", titulo: "Minha IA", resumo: "Claude, ChatGPT, Gemini ou Grok cuidando da agenda.", icone: sv(["M4 8h16v12H4z", "M12 8V4.5", "M9.5 13.5h.01", "M14.5 13.5h.01", "M9.5 17h5"]) },
  { id: "outlook", titulo: "Outlook ou outro", resumo: "Só para ver a agenda, sem mudar nada por lá.", icone: sv(["M2.5 12s3.5-6.5 9.5-6.5 9.5 6.5 9.5 6.5-3.5 6.5-9.5 6.5S2.5 12 2.5 12z", "M12 9a3 3 0 1 1 0 6 3 3 0 0 1 0-6z"]) },
];

const PERGUNTAS = [
  ["O que é esse tal de MCP?", "É o endereço que deixa uma IA conversar com a sua agenda, como uma tomada. A IA entra com o seu login e só faz o que você liberou, com as mesmas regras da tela."],
  ["É seguro?", "A conexão é só sua: cada pessoa conecta a própria agenda. Tudo o que uma IA faz fica no histórico com o nome dela e pode ser desfeito em até 24 horas. Para cortar o acesso, use Desconectar em Integrações de IA."],
  ["Mudei no celular e não apareceu. E agora?", "No Google, a mudança chega em até 1 minuto. No iPhone e no Mac, o aparelho busca as novidades de tempos em tempos; abrir o app Calendário e puxar a lista de calendários para baixo atualiza na hora. O link só para ver pode levar algumas horas no Google."],
  ["Como desligo?", "Google e iPhone/Mac: Configurações › Calendário no celular. IAs: Configurações › Integrações de IA. Desligar não apaga nada no ERP."],
];

function useEstado(request) {
  const [estado, setEstado] = useState({ carregando: true });
  const carregar = useCallback(async () => {
    const integracoes = await lerMinhasIntegracoes(request).catch(() => null);
    const liberada = Boolean(integracoes?.agenda_liberada);
    const calendarios = liberada ? await lerCalendarios(request).catch(() => null) : null;
    const novo = { carregando: false, integracoes, liberada, calendarios };
    setEstado(novo);
    return novo;
  }, [request]);
  useEffect(() => { carregar(); }, [carregar]);
  return [estado, carregar, setEstado];
}

function Escolha({ caminho, onEscolher }) {
  return (
    <div className="gc-caminhos">
      {CAMINHOS.map((c) => (
        <button
          key={c.id} type="button" aria-pressed={caminho === c.id}
          className={`gc-caminho${caminho === c.id ? " ativo" : ""}`} onClick={() => onEscolher(c.id)}
        >
          <span className="gc-caminho-i">{c.icone}</span>
          <span className="gc-caminho-t"><b>{c.titulo}</b><small>{c.resumo}</small></span>
        </button>
      ))}
    </div>
  );
}

function CaminhoApple({ request, calendarios, onMudou, conferir }) {
  const [aparelho, setAparelho] = useState("iphone");
  const grupos = PASSOS_CALDAV.filter((g) => g.id !== "outros");
  const [resultado, setResultado] = useState(null);
  const caldav = calendarios.caldav;
  return (
    <ol className="gc-passos">
      <BlocoSenha numero={1} request={request} caldav={caldav} onMudou={onMudou} />
      <Passo numero={2} titulo="Cadastre a conta no aparelho">
        <Abas grupos={grupos} rotulo="Qual aparelho" valor={aparelho} onTrocar={setAparelho} />
        <Frases passos={grupos.find((g) => g.id === aparelho).passos} trocas={{ servidor: caldav.servidor }} />
      </Passo>
      <Conferir numero={3} resultado={resultado} onConferir={async () => setResultado(resultadoDoCalDAV((await conferir())?.calendarios?.caldav))} />
    </ol>
  );
}

function CaminhoGoogle({ request, calendarios, conferir, comAlternativa = false, onMudou }) {
  const [resultado, setResultado] = useState(null);
  const [semGoogle, setSemGoogle] = useState(false);
  const davx = PASSOS_CALDAV.find((g) => g.id === "outros");
  return (
    <>
      {comAlternativa ? <p className="gc-intro">O jeito mais fácil no Android é pelo Google Agenda: o calendário “Pharu ERP” aparece no app que já vem no celular.</p> : null}
      <ol className="gc-passos">
        <BlocoGoogle numero={1} request={request} google={calendarios.google} />
        <Conferir numero={2} resultado={resultado} onConferir={async () => setResultado(resultadoDoGoogle((await conferir())?.calendarios?.google))} />
      </ol>
      {comAlternativa ? (
        <div className="gc-alternativa">
          <button type="button" className="gc-link-btn" aria-expanded={semGoogle} onClick={() => setSemGoogle((v) => !v)}>
            Prefere sem Google? Use o app DAVx⁵
          </button>
          {semGoogle ? (
            <ol className="gc-passos">
              <BlocoSenha numero={1} request={request} caldav={calendarios.caldav} onMudou={onMudou} />
              <Passo numero={2} titulo="Cadastre a conta no DAVx⁵">
                <Frases passos={davx.passos.slice(0, 1)} trocas={{ servidor: calendarios.caldav.servidor }} />
              </Passo>
            </ol>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

function CaminhoIa({ integracoes, qual, onQual, conferir }) {
  const [resultado, setResultado] = useState(null);
  const ia = PASSOS_POR_IA.find((p) => p.id === qual) || PASSOS_POR_IA[0];
  const endereco = integracoes.endereco_mcp;
  return (
    <>
      <Abas grupos={PASSOS_POR_IA} rotulo="Qual IA" valor={ia.id} onTrocar={onQual} />
      <p className="gc-requisito">{ia.requisito}</p>
      {ia.novo ? <p className="gc-intro">Novidade de 30/10/2026. Se aparecer erro ao conectar, avise a Pharu.</p> : null}
      <ol className="gc-passos">
        <Passo numero={1} titulo="Copie o endereço da sua agenda">
          <div className="ia-endereco">
            <code title={endereco}>{endereco}</code>
            <BotaoCopiar texto={endereco} rotulo="Copiar" aria-label="Copiar o endereço do MCP" />
          </div>
        </Passo>
        <Passo numero={2} titulo={`Siga no ${ia.nome}`}>
          <Frases key={ia.id} passos={ia.passos} trocas={{ url: endereco }} />
          <p className="gc-dica">{DICA_DOS_MENUS}</p>
        </Passo>
        <Conferir numero={3} resultado={resultado} onConferir={async () => setResultado(resultadoDaIa((await conferir())?.integracoes))} />
      </ol>
    </>
  );
}

function CaminhoOutlook() {
  const [onde, setOnde] = useState("outlook");
  const grupos = ["outlook", "google", "apple"].map((id) => PASSOS_POR_CALENDARIO.find((g) => g.id === id));
  return (
    <ol className="gc-passos">
      <Passo numero={1} titulo="Gere o seu link">
        <p>Em <Link to={TELA_CALENDARIO}>Calendário no celular</Link>, na parte “Link para só ver (.ics)”, clique em gerar e copie o link.</p>
      </Passo>
      <Passo numero={2} titulo="Cole o link no seu calendário">
        <Abas grupos={grupos} rotulo="Qual calendário" valor={onde} onTrocar={setOnde} />
        <Frases passos={grupos.find((g) => g.id === onde).passos} />
      </Passo>
    </ol>
  );
}

export default function GuiaConectar() {
  const { request } = useAuth();
  const [params, setParams] = useSearchParams();
  const [estado, carregar, setEstado] = useEstado(request);
  const caminho = CAMINHOS.some((c) => c.id === params.get("caminho")) ? params.get("caminho") : null;
  const qual = params.get("qual") || PASSOS_POR_IA[0].id;
  const trocar = (mudancas) => setParams((atual) => {
    const proximo = new URLSearchParams(atual);
    Object.entries(mudancas).forEach(([chave, valor]) => (valor ? proximo.set(chave, valor) : proximo.delete(chave)));
    return proximo;
  }, { replace: true });
  const aoGerarSenha = (resposta) => setEstado((e) => ({ ...e, calendarios: resposta }));
  // No celular os passos ficam abaixo dos cinco cartões: desce até eles.
  const secaoRef = useRef(null);
  useEffect(() => {
    const secao = secaoRef.current;
    if (!caminho || !secao || secao.getBoundingClientRect().top < window.innerHeight * 0.6) return;
    const suave = !window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    secao.scrollIntoView({ behavior: suave ? "smooth" : "auto", block: "start" });
  }, [caminho]);

  const precisaCalendario = caminho && caminho !== "ia" && caminho !== "outlook";
  let conteudo = null;
  if (estado.carregando) conteudo = <div aria-busy="true" className="ia-carregando">Carregando…</div>;
  else if (!estado.liberada) conteudo = <p className="ia-aviso">A agenda nova ainda não foi liberada para você. Quando for, é só voltar aqui.</p>;
  else if (precisaCalendario && !estado.calendarios) conteudo = <p className="ia-erro">Não deu para ler os seus calendários agora. Tente de novo em instantes.</p>;
  else if (caminho === "apple") conteudo = <CaminhoApple request={request} calendarios={estado.calendarios} onMudou={aoGerarSenha} conferir={carregar} />;
  else if (caminho === "android") conteudo = <CaminhoGoogle request={request} calendarios={estado.calendarios} onMudou={aoGerarSenha} conferir={carregar} comAlternativa />;
  else if (caminho === "google") conteudo = <CaminhoGoogle request={request} calendarios={estado.calendarios} conferir={carregar} />;
  else if (caminho === "ia") conteudo = <CaminhoIa integracoes={estado.integracoes} qual={qual} onQual={(id) => trocar({ qual: id })} conferir={carregar} />;
  else if (caminho === "outlook") conteudo = <CaminhoOutlook />;
  const escolhido = CAMINHOS.find((c) => c.id === caminho);

  return (
    <div className="gc-tela">
      <section className="set-section">
        <div className="set-section__head">
          <h2 className="set-section__title">Onde você quer a sua agenda?</h2>
          <p className="set-section__desc">Escolha um caminho. São poucos passos, um de cada vez, e você consegue fazer sozinho.</p>
        </div>
        <div className="set-section__body">
          <Escolha caminho={caminho} onEscolher={(id) => trocar({ caminho: id, qual: id === "ia" ? qual : null })} />
        </div>
      </section>

      {escolhido ? (
        <section className="set-section gc-caminho-aberto" key={caminho} ref={secaoRef}>
          <div className="set-section__head">
            <h2 className="set-section__title">{escolhido.titulo}</h2>
            <p className="set-section__desc">{escolhido.resumo}</p>
          </div>
          <div className="set-section__body">{conteudo}</div>
        </section>
      ) : null}

      {caminho && caminho !== "ia" && caminho !== "outlook" && estado.liberada ? (
        <section className="set-section">
          <div className="set-section__head"><h2 className="set-section__title">Como usar pelo celular</h2></div>
          <div className="set-section__body">
            <ul className="cal-regras">{REGRAS_DA_VIA_DUPLA.map((regra) => <li key={regra}>{regra}</li>)}</ul>
          </div>
        </section>
      ) : null}

      <section className="set-section">
        <div className="set-section__head"><h2 className="set-section__title">Perguntas rápidas</h2></div>
        <div className="set-section__body gc-perguntas">
          {PERGUNTAS.map(([pergunta, resposta]) => (
            <details key={pergunta}>
              <summary>{pergunta}</summary>
              <p>{resposta}</p>
            </details>
          ))}
          <p className="gc-rodape">
            Para gerenciar o que já está conectado: <Link to={TELA_CALENDARIO}>Calendário no celular</Link> e <Link to={TELA_IA}>Integrações de IA</Link>.
          </p>
        </div>
      </section>
    </div>
  );
}
